// src/pages/CreateUserPage.tsx
import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress,
  Container,
  Divider,
  Stack,
  SelectChangeEvent,
  Grid,
  InputAdornment,
  Autocomplete,
  Chip,
} from '@mui/material';
import { PersonAdd as PersonAddIcon, Save as SaveIcon, Person as PersonIcon } from '@mui/icons-material';
import { api } from '../services/api';
import { UserRole } from '../types/chat';
import { useRole } from '../hooks/useRole';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { countries } from '../shared/countries';
import { validateTCKN, validatePassport, isTurkishNationality } from '../shared/validators';

interface Room {
  id: string;
  roomNumber: string;
  roomType: string;
  floor: string;
  status: string;
}

interface CreateUserFormData {
  email: string;
  password: string;
  confirmPassword: string;
  firstName: string;
  lastName: string;
  role: UserRole;
  phoneNumber?: string;
  birthdate?: string; // YYYY-MM-DD format
  nationalId?: string;
  nationality?: string;
  gender?: string;
  country?: string;
  city?: string;
  postCode?: string;
  passportId?: string;
  roomId?: string; // For guests (single room)
  roomIds?: string[]; // For operators (multiple rooms)
  checkInDate?: string;
  checkOutDate?: string;
  confirmationNumber?: string;
  profileId?: string; // PMS Profile ID for magic link generation
}

const CreateUserPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { isAdmin } = useRole();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [magicLink, setMagicLink] = useState<string | null>(null);
  const [copySuccess, setCopySuccess] = useState(false);
  const [magicLinkKey, setMagicLinkKey] = useState(0); // Key to force re-render of magic link card
  const [rooms, setRooms] = useState<Room[]>([]);
  const [loadingRooms, setLoadingRooms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  
  const [formData, setFormData] = useState<CreateUserFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    role: UserRole.OPERATOR,
    phoneNumber: '',
    birthdate: '',
    nationalId: '',
    nationality: '',
    gender: '',
    country: '',
    city: '',
    postCode: '',
    passportId: '',
    roomId: '',
    roomIds: [],
    checkInDate: '',
    checkOutDate: '',
    confirmationNumber: '',
    profileId: '',
  });

  // Get country name based on current language
  const getCountryName = (country: typeof countries[0]) => {
    const lang = i18n.language.split('-')[0];
    switch (lang) {
      case 'tr': return country.nameTr || country.name;
      case 'de': return country.nameDe || country.name;
      case 'fr': return country.nameFr || country.name;
      case 'ru': return country.nameRu || country.name;
      case 'ar': return country.nameAr || country.name;
      default: return country.name;
    }
  };

  // Validation functions
  const validateName = (value: string): boolean => {
    // Only allow letters, spaces, hyphens, and apostrophes
    const nameRegex = /^[a-zA-ZÀ-ÿĀ-žА-яА-Я\s'-]+$/;
    return nameRegex.test(value);
  };

  const validatePhone = (value: string): boolean => {
    // Only allow + at the beginning, then digits, no spaces
    const phoneRegex = /^\+?[0-9]*$/;
    if (value.includes(' ')) {
      return false;
    }
    if (value.startsWith('+') && value.length > 1) {
      return phoneRegex.test(value);
    }
    if (!value.startsWith('+')) {
      return phoneRegex.test(value);
    }
    return true;
  };

  const validateBirthdate = (value: string): boolean => {
    if (!value) return true; // Optional field
    const selectedDate = new Date(value);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return selectedDate <= today;
  };

  // Fetch rooms when component mounts
  React.useEffect(() => {
    fetchRooms();
  }, []);

  const fetchRooms = async () => {
    try {
      setLoadingRooms(true);
      const response = await api.get('/rooms');
      setRooms(response.data);
    } catch (err: any) {
      console.error('Failed to fetch rooms:', err);
    } finally {
      setLoadingRooms(false);
    }
  };

  // Redirect if not admin
  if (!isAdmin) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{t('users.noPermission')}</Alert>
      </Container>
    );
  }

  const handleChange = (field: string, value: any) => {
    // Clear field error when user starts typing
    if (fieldErrors[field]) {
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Apply validation based on field type
    if (field === 'firstName' || field === 'lastName') {
      if (value && !validateName(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: t('createUser.onlyLetters')
        }));
        return;
      }
    }

    if (field === 'phoneNumber') {
      if (value && !validatePhone(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: t('createUser.phoneFormat')
        }));
        return;
      }
    }

    if (field === 'birthdate') {
      if (value && !validateBirthdate(value)) {
        setFieldErrors(prev => ({
          ...prev,
          [field]: t('createUser.birthdateFuture')
        }));
        return;
      }
    }

    setFormData(prev => ({ ...prev, [field]: value }));
    if (error) setError(null);
    if (success) setSuccess(null);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<UserRole> | SelectChangeEvent<string>) => {
    const { name, value } = e.target;
    
    // If role is changing and it's not GUEST, clear guest-specific fields
    if (name === 'role' && value !== UserRole.GUEST) {
      setFormData(prev => ({
        ...prev,
        role: value as UserRole,
        phoneNumber: '',
        birthdate: '',
        nationalId: '',
        nationality: '',
        gender: '',
        country: '',
        city: '',
        postCode: '',
        passportId: '',
      }));
      // Clear field errors for guest-specific fields
      setFieldErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors.phoneNumber;
        delete newErrors.birthdate;
        delete newErrors.nationality;
        delete newErrors.country;
        return newErrors;
      });
    } else {
      handleChange(name as string, value);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.email || !formData.password || !formData.firstName || !formData.lastName) {
      setError(t('createUser.requiredFields'));
      return false;
    }

    if (!validateName(formData.firstName)) {
      errors.firstName = t('createUser.firstNameLettersOnly');
    }

    if (!validateName(formData.lastName)) {
      errors.lastName = t('createUser.lastNameLettersOnly');
    }

    if (formData.password !== formData.confirmPassword) {
      setError(t('createUser.passwordsDoNotMatch'));
      return false;
    }

    if (formData.password.length < 6) {
      setError(t('createUser.passwordMinLength'));
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError(t('createUser.validEmail'));
      return false;
    }

    // Only validate guest-specific fields if role is GUEST
    if (formData.role === UserRole.GUEST) {
      if (formData.phoneNumber && !validatePhone(formData.phoneNumber)) {
        errors.phoneNumber = t('createUser.invalidPhoneFormat');
      }

      if (formData.birthdate && !validateBirthdate(formData.birthdate)) {
        errors.birthdate = t('createUser.birthdateFuture');
      }

      if (formData.nationality && !countries.find(c => c.code === formData.nationality)) {
        errors.nationality = t('createUser.invalidNationality');
      }

      if (formData.country && !countries.find(c => c.code === formData.country)) {
        errors.country = t('createUser.invalidCountry');
      }

      // National ID (T.C.) required for Turkish nationality; passport otherwise.
      const isTR = isTurkishNationality(formData.nationality);
      if (formData.nationality && formData.nationality.trim()) {
        if (isTR) {
          if (!formData.nationalId || !formData.nationalId.trim()) {
            errors.nationalId = t('validation.nationalIdRequired');
          } else if (!validateTCKN(formData.nationalId)) {
            errors.nationalId = t('validation.invalidTckn');
          }
        } else if (!formData.passportId || !formData.passportId.trim()) {
          errors.passportId = t('validation.passportRequired');
        } else if (!validatePassport(formData.passportId, formData.nationality)) {
          errors.passportId = t('validation.invalidPassport');
        }
      }
    }

    // Room assignment is required for operators
    if (formData.role === UserRole.OPERATOR && (!formData.roomIds || formData.roomIds.length === 0)) {
      setError(t('createUser.roomRequiredOperator'));
      return false;
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    setMagicLink(null); // Clear previous magic link when starting new submission
    setCopySuccess(false);
    setMagicLinkKey(prev => prev + 1); // Force re-render of magic link card

    try {
      const { confirmPassword, ...userData } = formData;
      
      // For operators and admins, only include: firstName, lastName, email, password, role, roomId (operators only)
      // For guests, include all fields
      const cleanedData: any = {
        firstName: userData.firstName,
        lastName: userData.lastName,
        email: userData.email,
        password: userData.password,
        role: userData.role,
      };

      // Room assignment - required for operators (multiple rooms), optional for guests (single room)
      if (userData.role === UserRole.OPERATOR) {
        cleanedData.roomIds = userData.roomIds || [];
      } else if (userData.role === UserRole.GUEST) {
        cleanedData.roomId = userData.roomId || undefined;
      }

      // Only include guest-specific fields if role is GUEST
      if (userData.role === UserRole.GUEST) {
        cleanedData.phoneNumber = userData.phoneNumber || undefined;
        cleanedData.birthdate = userData.birthdate || undefined;
        cleanedData.nationalId = userData.nationalId || undefined;
        cleanedData.nationality = userData.nationality || undefined;
        cleanedData.gender = userData.gender || undefined;
        cleanedData.country = userData.country || undefined;
        cleanedData.city = userData.city || undefined;
        cleanedData.postCode = userData.postCode || undefined;
        cleanedData.passportId = userData.passportId || undefined;
        cleanedData.checkInDate = userData.checkInDate || undefined;
        cleanedData.checkOutDate = userData.checkOutDate || undefined;
        cleanedData.confirmationNumber = userData.confirmationNumber || undefined;
        cleanedData.profileId = userData.profileId || undefined;
      }
      
      const response = await api.post('/users', cleanedData);
      
      // If user is a guest and has a magic link, show it
      if (response.data.magicLink) {
        setMagicLink(response.data.magicLink);
        setMagicLinkKey(prev => prev + 1); // Force re-render with new magic link
      } else {
        // Ensure magic link is cleared if no magic link in response
        setMagicLink(null);
      }
      
      const roleName = formData.role === UserRole.ADMIN ? t('sidebar.admin') : formData.role === UserRole.OPERATOR ? t('sidebar.operator') : t('sidebar.guest');
      setSuccess(`${roleName} ${t('createUser.userCreatedSuccess')}`);
      
      // Reset form
      setFormData({
        email: '',
        password: '',
        confirmPassword: '',
        firstName: '',
        lastName: '',
        role: UserRole.OPERATOR,
        phoneNumber: '',
        birthdate: '',
        nationalId: '',
        nationality: '',
        gender: '',
        country: '',
        city: '',
        postCode: '',
        passportId: '',
        roomId: '',
        roomIds: [],
        checkInDate: '',
        checkOutDate: '',
        confirmationNumber: '',
        profileId: '',
      });
      setFieldErrors({});

      // Don't auto-redirect - let admin stay on page to create more users
      // Users can manually navigate away if needed
    } catch (err: any) {
      setError(err.response?.data?.message || t('createUser.failedToCreate'));
    } finally {
      setLoading(false);
    }
  };

  const handleReset = () => {
    setFormData({
      email: '',
      password: '',
      confirmPassword: '',
      firstName: '',
      lastName: '',
      role: UserRole.OPERATOR,
      phoneNumber: '',
    birthdate: '',
    nationalId: '',
    nationality: '',
    gender: '',
    country: '',
    city: '',
    postCode: '',
    passportId: '',
    roomId: '',
      checkInDate: '',
      checkOutDate: '',
      confirmationNumber: '',
      profileId: '',
    });
    setError(null);
    setSuccess(null);
    setMagicLink(null);
    setCopySuccess(false);
    setMagicLinkKey(0); // Reset magic link key
    setFieldErrors({});
  };

  return (
    <Container maxWidth="md" sx={{ py: 4, height: '100vh', overflow: 'auto' }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <PersonAddIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" gutterBottom>
              {t('createUser.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {t('createUser.subtitle')}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 3 }} />

        {error && (
          <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccess(null)}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            {/* Role Selection */}
            <FormControl fullWidth required>
              <InputLabel>{t('createUser.userRole')}</InputLabel>
              <Select
                name="role"
                value={formData.role}
                onChange={handleInputChange}
                label={t('createUser.userRole')}
              >
                <MenuItem value={UserRole.OPERATOR}>
                  <Box>
                    <Typography variant="body1">{t('createUser.operator')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('createUser.operatorDesc')}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={UserRole.ADMIN}>
                  <Box>
                    <Typography variant="body1">{t('createUser.admin')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('createUser.adminDesc')}
                    </Typography>
                  </Box>
                </MenuItem>
                <MenuItem value={UserRole.GUEST}>
                  <Box>
                    <Typography variant="body1">{t('createUser.guest')}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {t('createUser.guestDesc')}
                    </Typography>
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Name Fields */}
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('login.firstName')}
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  disabled={loading}
                  error={!!fieldErrors.firstName}
                  helperText={fieldErrors.firstName}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonIcon sx={{ color: '#667eea', fontSize: '1.25rem' }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  label={t('login.lastName')}
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  disabled={loading}
                  error={!!fieldErrors.lastName}
                  helperText={fieldErrors.lastName}
                />
              </Grid>
            </Grid>

            {/* Email */}
            <TextField
              fullWidth
              required
              type="email"
              label={t('login.email')}
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              disabled={loading}
              placeholder="user@hotel.com"
            />

            {/* Guest-specific fields - only show for GUEST role */}
            {formData.role === UserRole.GUEST && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('createUser.guestInformation')}
                  </Typography>
                </Divider>

                {/* Phone Number */}
                <TextField
                  fullWidth
                  label={t('login.phoneNumber')}
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={(e) => {
                    let value = e.target.value;
                    // Remove spaces
                    value = value.replace(/\s/g, '');
                    // Only allow + at the beginning
                    if (value.includes('+') && value.indexOf('+') !== 0) {
                      value = value.replace(/\+/g, '');
                    }
                    handleChange('phoneNumber', value);
                  }}
                  disabled={loading}
                  error={!!fieldErrors.phoneNumber}
                  helperText={fieldErrors.phoneNumber || t('personalSettings.phoneFormat')}
                  inputProps={{
                    maxLength: 20,
                  }}
                />

                {/* Birthdate */}
                <TextField
                  fullWidth
                  type="date"
                  label={t('login.birthdate')}
                  name="birthdate"
                  value={formData.birthdate}
                  onChange={handleInputChange}
                  disabled={loading}
                  error={!!fieldErrors.birthdate}
                  helperText={fieldErrors.birthdate}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{
                    max: new Date().toISOString().split('T')[0], // Prevent future dates
                  }}
                />

                {/* National ID */}
                <TextField
                  fullWidth
                  required={isTurkishNationality(formData.nationality)}
                  label={t('login.nationalId')}
                  name="nationalId"
                  value={formData.nationalId}
                  onChange={handleInputChange}
                  disabled={loading}
                  error={!!fieldErrors.nationalId}
                  helperText={fieldErrors.nationalId}
                  inputProps={{ maxLength: 11 }}
                />

                {/* Nationality */}
                <FormControl fullWidth error={!!fieldErrors.nationality}>
                  <InputLabel>{t('login.nationality')}</InputLabel>
                  <Select
                    name="nationality"
                    value={formData.nationality}
                    label={t('login.nationality')}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {getCountryName(country)}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.nationality && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {fieldErrors.nationality}
                    </Typography>
                  )}
                </FormControl>

                {/* Gender */}
                <FormControl fullWidth>
                  <InputLabel>{t('login.gender')}</InputLabel>
                  <Select
                    name="gender"
                    value={formData.gender}
                    label={t('login.gender')}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    <MenuItem value="M">{t('login.male')}</MenuItem>
                    <MenuItem value="F">{t('login.female')}</MenuItem>
                    <MenuItem value="Other">{t('login.other')}</MenuItem>
                  </Select>
                </FormControl>

                {/* Country */}
                <FormControl fullWidth error={!!fieldErrors.country}>
                  <InputLabel>{t('login.country')}</InputLabel>
                  <Select
                    name="country"
                    value={formData.country}
                    label={t('login.country')}
                    onChange={handleInputChange}
                    disabled={loading}
                  >
                    {countries.map((country) => (
                      <MenuItem key={country.code} value={country.code}>
                        {getCountryName(country)}
                      </MenuItem>
                    ))}
                  </Select>
                  {fieldErrors.country && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, ml: 1.75 }}>
                      {fieldErrors.country}
                    </Typography>
                  )}
                </FormControl>

                {/* City and Post Code */}
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t('login.city')}
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={t('login.postCode')}
                      name="postCode"
                      value={formData.postCode}
                      onChange={handleInputChange}
                      disabled={loading}
                    />
                  </Grid>
                </Grid>

                {/* Passport ID */}
                <TextField
                  fullWidth
                  required={!!formData.nationality && !isTurkishNationality(formData.nationality)}
                  label={t('login.passportId')}
                  name="passportId"
                  value={formData.passportId}
                  onChange={handleInputChange}
                  disabled={loading}
                  error={!!fieldErrors.passportId}
                  helperText={fieldErrors.passportId}
                />
              </>
            )}

            <Divider sx={{ my: 2 }}>
              <Typography variant="caption" color="text.secondary">
                {t('createUser.accountCredentials')}
              </Typography>
            </Divider>

            {/* Password Fields */}
            <TextField
              fullWidth
              required
              type="password"
              label={t('login.password')}
              name="password"
              value={formData.password}
              onChange={handleInputChange}
              disabled={loading}
              helperText={t('createUser.minimumChars')}
            />

            <TextField
              fullWidth
              required
              type="password"
              label={t('createUser.confirmPassword')}
              name="confirmPassword"
              value={formData.confirmPassword}
              onChange={handleInputChange}
              disabled={loading}
              error={formData.password !== formData.confirmPassword && formData.confirmPassword !== ''}
              helperText={
                formData.password !== formData.confirmPassword && formData.confirmPassword !== ''
                  ? t('createUser.passwordsDoNotMatch')
                  : ''
              }
            />

            {/* Guest and Operator room assignment fields */}
            {(formData.role === UserRole.GUEST || formData.role === UserRole.OPERATOR) && (
              <>
                <Divider sx={{ my: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {formData.role === UserRole.GUEST ? t('sidebar.guest') : t('sidebar.operator')} {t('rooms.title')}
                  </Typography>
                </Divider>

                {/* Multi-select for operators, single select for guests */}
                {formData.role === UserRole.OPERATOR ? (
                  <Autocomplete
                    multiple
                    options={rooms.filter(room => room.status === 'available')}
                    getOptionLabel={(room) => `Room ${room.roomNumber} - ${room.roomType} (Floor ${room.floor})`}
                    value={rooms.filter(room => formData.roomIds?.includes(room.id))}
                    onChange={(_, newValue) => {
                      handleChange('roomIds', newValue.map(room => room.id));
                    }}
                    disabled={loading || loadingRooms}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label={t('createUser.assignRoomRequired') + ' *'}
                        placeholder={formData.roomIds && formData.roomIds.length > 0 ? '' : 'Select rooms...'}
                        error={formData.role === UserRole.OPERATOR && (!formData.roomIds || formData.roomIds.length === 0) && error !== null}
                        helperText={formData.role === UserRole.OPERATOR && (!formData.roomIds || formData.roomIds.length === 0) && error ? t('createUser.roomRequiredOperator') : ''}
                      />
                    )}
                    renderTags={(value, getTagProps) =>
                      value.map((room, index) => {
                        const { key, ...tagProps } = getTagProps({ index });
                        return (
                          <Chip
                            key={room.id}
                            label={`Room ${room.roomNumber}`}
                            {...tagProps}
                            size="small"
                          />
                        );
                      })
                    }
                  />
                ) : (
                  <FormControl fullWidth>
                  <InputLabel>
                      {t('createUser.assignRoomAuto')}
                  </InputLabel>
                  <Select
                    name="roomId"
                    value={formData.roomId || ''}
                    onChange={handleInputChange}
                      label={t('createUser.assignRoomAuto')}
                    disabled={loading || loadingRooms}
                  >
                      <MenuItem value="">
                        <em>{t('createUser.autoAssignAvailable')}</em>
                      </MenuItem>
                    {rooms
                      .filter(room => room.status === 'available')
                      .map((room) => (
                        <MenuItem key={room.id} value={room.id}>
                          Room {room.roomNumber} - {room.roomType} (Floor {room.floor})
                        </MenuItem>
                      ))}
                  </Select>
                </FormControl>
                )}

                {formData.role === UserRole.GUEST && (
                  <>
                    <TextField
                      fullWidth
                      label={t('createUser.pmsProfileId')}
                      name="profileId"
                      value={formData.profileId || ''}
                      onChange={handleInputChange}
                      disabled={loading}
                      placeholder="4580889 (dev default)"
                      helperText={t('createUser.pmsProfileIdHelper')}
                    />
                    
                    <TextField
                      fullWidth
                      label={t('createUser.confirmationNumber')}
                      name="confirmationNumber"
                      value={formData.confirmationNumber || ''}
                      onChange={handleInputChange}
                      disabled={loading}
                      placeholder="4079922 (dev default)"
                      helperText={t('createUser.confirmationNumberHelper')}
                    />

                    <Grid container spacing={2}>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          type="date"
                          label={t('createUser.checkInDate')}
                          name="checkInDate"
                          value={formData.checkInDate || ''}
                          onChange={handleInputChange}
                          disabled={loading}
                          InputLabelProps={{ shrink: true }}
                          helperText={t('createUser.defaultsToToday')}
                        />
                      </Grid>
                      <Grid size={{ xs: 12, sm: 6 }}>
                        <TextField
                          fullWidth
                          type="date"
                          label={t('createUser.checkOutDate')}
                          name="checkOutDate"
                          value={formData.checkOutDate || ''}
                          onChange={handleInputChange}
                          disabled={loading}
                          InputLabelProps={{ shrink: true }}
                          helperText={t('createUser.defaultsTo7Days')}
                        />
                      </Grid>
                    </Grid>
                  </>
                )}
              </>
            )}

            {/* Action Buttons */}
            <Box sx={{ display: 'flex', gap: 2, mt: 2 }}>
              <Button
                type="submit"
                variant="contained"
                size="large"
                fullWidth
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SaveIcon />}
              >
                {loading ? t('createUser.creatingUser') : t('createUser.createUser')}
              </Button>
              <Button
                type="button"
                variant="outlined"
                size="large"
                onClick={handleReset}
                disabled={loading}
              >
                {t('createUser.reset')}
              </Button>
            </Box>
          </Stack>
        </form>

        {/* Info Box */}
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2" fontWeight="600" gutterBottom>
            {t('createUser.rolePermissions')}
          </Typography>
          <Typography variant="caption" component="div">
            • <strong>{t('sidebar.guest')}:</strong> {t('createUser.guestPermissions')}
          </Typography>
          <Typography variant="caption" component="div">
            • <strong>{t('sidebar.operator')}:</strong> {t('createUser.operatorPermissions')}
          </Typography>
          <Typography variant="caption" component="div">
            • <strong>{t('sidebar.admin')}:</strong> {t('createUser.adminPermissions')}
          </Typography>
        </Alert>

        {/* Magic Link Dialog */}
        {magicLink && (
          <Alert key={magicLinkKey} severity="success" sx={{ mt: 3 }}>
            <Typography variant="body2" fontWeight="600" gutterBottom>
              {t('createUser.guestCreatedMagicLink')}
            </Typography>
            <Box sx={{ 
              bgcolor: 'background.paper', 
              p: 2, 
              borderRadius: 1, 
              mt: 2,
              border: '1px solid',
              borderColor: 'success.main',
            }}>
              <Typography variant="body2" sx={{ wordBreak: 'break-all', fontFamily: 'monospace' }}>
                {magicLink}
              </Typography>
            </Box>
            <Typography variant="caption" display="block" sx={{ mt: 2 }}>
              {t('createUser.shareLink')}
            </Typography>
            <Button 
              variant="outlined" 
              size="small" 
              sx={{ mt: 2 }}
              onClick={async () => {
                if (magicLink) {
                  try {
                    await navigator.clipboard.writeText(magicLink);
                    setCopySuccess(true);
                    setTimeout(() => setCopySuccess(false), 2000);
                  } catch (err) {
                    // Fallback for browsers that don't support clipboard API
                    const textArea = document.createElement('textarea');
                    textArea.value = magicLink;
                    textArea.style.position = 'fixed';
                    textArea.style.left = '-999999px';
                    document.body.appendChild(textArea);
                    textArea.select();
                    try {
                      document.execCommand('copy');
                      setCopySuccess(true);
                      setTimeout(() => setCopySuccess(false), 2000);
                    } catch (fallbackErr) {
                      setError(t('createUser.copyFailed') || 'Failed to copy link');
                    }
                    document.body.removeChild(textArea);
                  }
                }
              }}
            >
              {copySuccess ? t('createUser.linkCopied') : t('createUser.copyLink')}
            </Button>
            <Button 
              variant="contained" 
              size="small" 
              sx={{ mt: 2, ml: 2 }}
              onClick={() => {
                setMagicLink(null);
                setMagicLinkKey(0);
                navigate('/dashboard');
              }}
            >
              {t('createUser.done')}
            </Button>
          </Alert>
        )}
      </Paper>
    </Container>
  );
};

export default CreateUserPage;

